'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { IconHome, IconHeart, IconSearch, IconChat, IconUser, IconEdit } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { useStore } from '@/providers/StoreProvider';
import { Badge } from '../ui';
import { useAnalytics } from '@/hooks/useAnalytics';

type NavItem = {
  key: string;
  label: string;
  href: string;
  Icon: (props: { width?: number; height?: number; color?: string }) => React.JSX.Element;
  isActive?: (pathname: string) => boolean;
  badge?: boolean;
  badgeCount?: number;
};

export function BottomNav() {
  const pathname = usePathname();
  const [hidden, setHidden] = useState(false);
  const analytics = useAnalytics();

  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const explorer = useStore(s => s.explorer.data);
  const unreadMessages = useStore(s => s.chat?.unreadMessages || 0);

  const navItems: NavItem[] = [
    {
      key: 'explore',
      label: 'Explore',
      href: '/',
      Icon: IconSearch,
      isActive: (pathname: string) => pathname === '/' || pathname === '/read-reviews',
    },
    {
      key: 'review',
      label: 'Review',
      href: '/review',
      Icon: IconEdit,
      isActive: (pathname: string) => pathname.startsWith('/review'),
    },
    {
      key: 'lease',
      label: 'Lease',
      href: '/profile/tenant-portal',
      Icon: IconHome,
      isActive: (pathname: string) => pathname.startsWith('/profile/tenant-portal'),
    },
    {
      key: 'messages',
      label: 'Messages',
      href: '/messages',
      Icon: IconChat,
      isActive: (pathname: string) => pathname.startsWith('/messages'),
      badge: unreadMessages > 0,
      badgeCount: unreadMessages,
    },
    {
      key: 'profile',
      label: 'Profile',
      href: '/profile',
      Icon: IconUser,
      isActive: (pathname: string) =>
        pathname.startsWith('/profile') && !pathname.startsWith('/profile/tenant-portal'),
      badge: !isLoggedIn || !explorer?.accessType,
    },
  ];

  return (
    <>
      <nav
        data-bottom-nav
        aria-label="Bottom Navigation"
        className={cn(
          'bottom-nav',
          'fixed inset-x-0 bottom-0 z-50 bg-primary shadow-reverse pb-[env(safe-area-inset-bottom)] md:hidden',
          hidden && 'translate-y-full opacity-0 pointer-events-none',
        )}
      >
        <div className="mx-auto grid grid-cols-5 px-6">
          {navItems.map(({ key, label, href, Icon, isActive, badge, badgeCount }) => {
            const active = isActive ? isActive(pathname) : false;
            const handleClick = () => {
              // Don't fire analytics event if the nav item is already active
              if (active) return;

              switch (key) {
                case 'explore':
                  void analytics.track('bottom_nav_explore', {});
                  break;
                case 'review':
                  void analytics.track('bottom_nav_review', {});
                  break;
                case 'lease':
                  void analytics.track('bottom_nav_lease', {});
                  break;
                case 'messages':
                  void analytics.track('bottom_nav_messages', {});
                  break;
                case 'profile':
                  void analytics.track('bottom_nav_profile', {});
                  break;
              }
            };
            return (
              <Link
                key={key}
                href={href}
                onClick={handleClick}
                className="flex flex-col items-center justify-center gap-1 pt-3 pb-1 text-center"
              >
                <span
                  className={`flex relative items-center justify-center rounded-3xl px-4 py-2 ${
                    active ? 'bg-active' : ''
                  }`}
                >
                  <Icon
                    width={20}
                    height={20}
                    color={active ? 'var(--icon-color-accent)' : 'var(--icon-color-secondary)'}
                  />
                  {badge && (
                    <Badge
                      type="alert"
                      count={badgeCount || 0}
                      border
                      className={cn(
                        '!absolute',
                        badgeCount && badgeCount > 0 ? '-top-2 -right-1' : 'top-0 right-0',
                      )}
                    />
                  )}
                </span>
                <span
                  className={`${
                    active ? 'text-accent text-caption-high' : 'text-secondary text-caption'
                  }`}
                >
                  {label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
      {/* Spacer so content doesn't hide behind the fixed BottomNav on mobile */}
      <div
        aria-hidden="true"
        className={cn('md:hidden')}
        style={{ height: 'calc(64px + env(safe-area-inset-bottom))' }}
      />
    </>
  );
}
